
import { FoodDetailItem, FoodItem, listRestaurantType } from '../interfaces/types';
import { getCurrentJstDate, parseJstDateTime } from './jstDate';
import { GameDayData, isDeliveryRequestInPreOrderWindow } from './timeSlots';

/**
 * Parse "HH:mm" string into { hours, minutes }
 */
function parseTime(at: string | undefined): { hours: number; minutes: number } {
    if (!at) return { hours: 11, minutes: 30 };
    const [h, m] = at.split(':').map(Number);
    return { hours: isNaN(h) ? 11 : h, minutes: isNaN(m) ? 30 : m };
}


export interface PreOrderDisabledResult {
    disabled: boolean;
    reason?: 'bulk_off' | 'pre_order_off' | 'sold_out' | 'outside_acceptance_window' | 'outside_delivery_window';
}

export interface PreOrderQuantityStatus {
    limited: boolean;
    maxQuantity: number;
    soldQuantity: number;
    remainingQuantity: number;
    exceedsLimit: boolean;
}

export function getPreOrderQuantityStatus(
    food: Pick<FoodDetailItem, 'pre_order_quantity' | 'pre_order_sold'> | undefined,
    orderDate: string | null | undefined,
    requestedQuantity: number
): PreOrderQuantityStatus {
    const maxQuantity = Number(food?.pre_order_quantity || 0);
    if (!food || !orderDate || maxQuantity <= 0) {
        return {
            limited: false,
            maxQuantity: 0,
            soldQuantity: 0,
            remainingQuantity: Number.POSITIVE_INFINITY,
            exceedsLimit: false,
        };
    }

    const dateKey = orderDate.slice(0, 10);
    const soldQuantity = Number(food.pre_order_sold?.[dateKey] || 0);
    const remainingQuantity = Math.max(0, maxQuantity - soldQuantity);

    return {
        limited: true,
        maxQuantity,
        soldQuantity,
        remainingQuantity,
        exceedsLimit: requestedQuantity > remainingQuantity,
    };
}

export function getPreOrderQuantityStatusForCartItem(params: {
    food: Pick<FoodDetailItem, 'id' | 'food_id' | 'pre_order_quantity' | 'pre_order_sold'> | undefined;
    orderDate: string | null | undefined;
    cartFoods: FoodItem[];
    itemFoodId: string;
    newQuantity?: number;
    getMasterFoodId: (foodId: string) => string;
}): PreOrderQuantityStatus {
    const { food, orderDate, cartFoods, itemFoodId, newQuantity, getMasterFoodId } = params;
    const masterFoodId = getMasterFoodId(itemFoodId);
    const requestedQuantity = cartFoods.reduce((total, cartFood) => {
        if (getMasterFoodId(cartFood.item_food_id) !== masterFoodId) return total;
        const quantity = cartFood.item_food_id === itemFoodId && newQuantity !== undefined
            ? newQuantity
            : Number(cartFood.item_quantity || 0);
        return total + quantity;
    }, 0);

    return getPreOrderQuantityStatus(food, orderDate, requestedQuantity);
}

/**
 * Checks whether a food item should be DISABLED in pre-order mode.
 *
 * Conditions (all pre-order menu only):
 *  1. Bulk off: restaurant.enabled_pre_order === false
 *  2. Item off: food.enabled_pre_order !== true
 *  3. Sold out: food.sold >= food.pre_order_quantity (when pre_order_quantity set)
 *  4. Outside acceptance window per preorder_rules:
 *       - openTime  = gameDate - since.time days, at since.at time → pre-order OPENS
 *       - closeTime = gameDate - until.time days, at until.at time → pre-order CLOSES
 *       - Disabled if now < openTime OR now > closeTime
 *
 * @param food          - food item to check
 * @param restaurant    - restaurant data (includes preorder_rules & enabled_pre_order)
 * @param orderDate     - the game/order date (ISO string), used as reference for the time window
 * @param isPreOrder    - whether the current session is a pre-order session
 */
export function isPreOrderDisabled(
    food: FoodDetailItem,
    restaurant: listRestaurantType | undefined,
    orderDate: string | null | undefined,
    isPreOrder: boolean,
    deliveryRequest?: string,
    gameDayData?: GameDayData | null
): PreOrderDisabledResult {
    if (!isPreOrder) return { disabled: false };
    if (restaurant?.enabled_pre_order === false) {
        return { disabled: true, reason: 'bulk_off' };
    }
    if (!food.enabled_pre_order) {
        return { disabled: true, reason: 'pre_order_off' };
    }

    const quantityStatus = getPreOrderQuantityStatus(food, orderDate, 1);
    if (quantityStatus.limited && quantityStatus.remainingQuantity <= 0) {
        return { disabled: true, reason: 'sold_out' };
    }

    if (orderDate && restaurant?.preorder_rules?.acceptance) {
        const { since, until } = restaurant.preorder_rules.acceptance;
        const now = getCurrentJstDate();

        if (since && until) {
            const { hours: sinceH, minutes: sinceM } = parseTime(since.at);
            const { hours: untilH, minutes: untilM } = parseTime(until.at);

            // openTime = gameDate - since.time days, at since.at time
            const openTime = parseJstDateTime(orderDate, sinceH, sinceM);
            openTime.setDate(openTime.getDate() - since.time);

            // closeTime = gameDate - until.time days, at until.at time
            const closeTime = parseJstDateTime(orderDate, untilH, untilM);
            closeTime.setDate(closeTime.getDate() - until.time);

            if (now < openTime || now > closeTime) {
                return { disabled: true, reason: 'outside_acceptance_window' };
            }
        }
    }

    if (
        gameDayData &&
        restaurant?.preorder_rules &&
        !isDeliveryRequestInPreOrderWindow(gameDayData, deliveryRequest, restaurant.preorder_rules)
    ) {
        return { disabled: true, reason: 'outside_delivery_window' };
    }

    return { disabled: false };
}
